<template>
  <div
    :class="videoClass"
    :style="config.style"
    @click="outerPlayHandler"
  >
    <div
      v-if="isShowVideoImgWrapper"
      class="img_wrapper"
      @click="playHandler"
    >
      <img
        :src="config.videoConfig && config.videoConfig.poster || defaultPoster"
        alt=""
      >
      <div
        v-if="config.showPlayIcon"
        class="icon_play"
      >
        <img
          src="//mat1.gtimg.com/pingjs/js/TIAP/caiyun/images/icon-play.png"
          alt=""
        >
      </div>
    </div>
    <div
      v-if="config.videoType === videoTypes.transparentHotInline"
      :class="[inMagic?'img_wrapper__transparent':'img_wrapper__none']"
      @click="playHandler"
    />
    <div
      :id="videoId"
      class="video_wrapper"
      :style="videoStyle"
    />
  </div>
</template>

<script>
/**
 * 播放器文档: https://vm.gtimg.cn/thumbplayer/txplayerdoc/src/jsapi/apis.html
 *
 **/
import { asyncLoadJs } from '@tencent/magic-ui-util';
import componentMixin from '@tencent/magic-ui-util/mixins/component';
import renderView from '../../../src/mixins/renderView';

/**
 * 是否加载完js
 **/
let isTxplayerLoaded = false;

/**
 * 是否首次请求播放器
 * 请求失败时会重置
 **/
let isFirstTimeToLoadTxplayer = true;

// 播放器容器前缀
const playerContainerPrefix = 'magic_ui_video_';

const logger = (...args) => {
  console.log('[comp:magic-ui-video]', ...args);
};

/**
 * 播放状态
 *
 **/
const playState = {
  /**
   * 未开始 -1
   **/
  noStart: -1,

  /**
   * 已结束 0
   **/
  end: 0,

  /**
   * 播放中 1
   **/
  playing: 1,

  /**
   * 已暂停 2
   **/
  pause: 2,

  /**
   * 加载中 3
   **/
  loading: 3,
};

/**
 * 视频类型
 **/
const videoTypes = {

  /**
   * 内嵌视频 1
   **/
  inline: 1,

  /**
   * 弹出视频 2
   **/
  popup: 2,

  /**
   * 透明热区内嵌视频 3
   **/
  transparentHotInline: 3,
};

export default {
  name: 'MagicUiVideo',

  mixins: [renderView, componentMixin],

  magicTriggerEvents: [{
    label: '播放器全屏切换',
    value: 'fullScreenChange',
  }],

  inject: {
    app: {
      default: {},
    },
  },

  props: {
    config: {
      type: Object,
      default() {
        return {};
      },
    },
  },

  data() {
    return {
      player: null,
      defaultPoster: '',
      videoId: `${playerContainerPrefix}${this.config.id}`,
      isPlayed: false, // 是否播放过
      currentLoopPlayCount: 0, // 当前循环播放的次数
      videoTypes,
    };
  },

  computed: {
    videoStyle() {
      if ([videoTypes.popup, videoTypes.transparentHotInline].includes(this.config.videoType) || this.inMagic) {
        return {
          'z-index': this.isPlayed ? 1 : -1,
          position: 'absolute',
          top: 0,
          left: 0,
        };
      }
      return {};
    },

    videoShow() {
      return this.config.videoType !== videoTypes.transparentHotInline || this.isPlayed;
    },

    videoClass() {
      const { inMagic, isPlayed, config } = this;
      const { videoConfig } = config;
      const classes = ['magic-ui-video'];

      if (inMagic || isPlayed) {
        classes.push('magic-ui-video--play');
      }

      const { isHideUIControl, videoObjectFit, isDisableInteractMod } = videoConfig;

      if (isHideUIControl) {
        classes.push('hide_ui_control');
      }

      if (videoObjectFit) {
        classes.push(`video_object_fit_${videoObjectFit}`);
      }

      if (isDisableInteractMod) {
        classes.push('disable_interact');
      }

      return classes;
    },

    isShowVideoImgWrapper() {
      const { config, inMagic, videoTypes } = this;
      return config.videoType === videoTypes.popup || (config.videoType === videoTypes.inline && inMagic);
    },
  },

  watch: {
    'config.videoConfig'() {
      if (this.inMagic) return false;
      this.initPlayer();
    },
  },

  async created() {
    try {
      if (this.inMagic || this.inPrerender) {
        return;
      }

      // 这里使用 setInterval 去判断并加载 txplayer
      // 当页面有多个 video 实例的时候，避免重复加载 txplayer
      let last$ = null;
      const loadTxplayerInterval = setInterval(() => {
        if (window.$ === last$) {
          if (!isTxplayerLoaded && isFirstTimeToLoadTxplayer) {
            logger('start load txplayer');

            // 同时兼容 pc 和 mobile
            window.jQuery = window.$;

            // 这里需要马上置为false，
            // 为了只让第一个created组件去加载播放器
            isFirstTimeToLoadTxplayer = false;

            asyncLoadJs('//vm.gtimg.cn/tencentvideo/txp/js/txplayer.js').then(() => {
              logger('txplayer loaded');
              isTxplayerLoaded = true;
            })
              .catch(() => {
                logger('txplayer load error');
                // 播放器加载出错时置为 true，
                // 以便别的组件进入判断时还是能重新加载播放器
                isFirstTimeToLoadTxplayer = true;
              });
          }
          clearInterval(loadTxplayerInterval);
        } else {
          logger('window.$ is not equal to txplayer\'s $，reset window.$');
          last$ = window.$;
        }
      }, 200);
    } catch (err) {
      logger('hook:created, load txplayer error: ', err);
    }
  },

  async mounted() {
    logger('hook:mounted');
    if (this.inMagic || this.inPrerender) {
      return;
    }

    // 这里判断是否存在Txplayer
    // 有时候Txplayer并不能及时加载，
    // 因此这里用了 setInterval 去定时轮询，确保能初始化成功
    if (!window.Txplayer) {
      logger('txplayer is not exist, trying interval');
      const txplayerExistsInterval = setInterval(() => {
        if (window.Txplayer) {
          clearInterval(txplayerExistsInterval);
          logger('txplayer is exist, clear interval and start initPlayer');
          this.initPlayer();
        }
      }, 300);
    } else {
      logger('txplayer is exist, start initPlayer');
      this.initPlayer();
    }
  },

  methods: {
    initPlayer() {
      const { videoConfig, loopPlayConfig } = this.config;
      logger('method:initPlayer, videoConfig: ', videoConfig, ', loopPlayConfig: ', loopPlayConfig);

      if (!videoConfig.vid) {
        logger('vid is empty');
        return;
      }

      if (!this.player) {
        const playerConfig = {
          containerId: this.videoId,
          width: '100%',
          height: '100%',
          endPoster: true,
          isUsePreload: false,
          ...videoConfig,
        };

        if (videoConfig.isHideUIControl) {
          playerConfig.settings = {
            // PC web 下隐藏 UI 控件
            UiControl: {
              subPlugins: [],
            },
            // mobile h5 下隐藏 UI 控件
            H5UiControl: {
              subPlugins: [],
            },
          };
          // 隐藏画中画功能
          playerConfig.disablepip = 1;
        }

        logger('new Txplayer: ', playerConfig);
        this.player = new window.Txplayer(playerConfig);

        this.initPlayerStateChangeEvent();
        this.initPlayerFullscreenEvent();
        this.initAutoplayPolyfill();
      } else {
        this.player.play({
          ...videoConfig,
        });
        this.isPlayed = true;
      }
    },

    initPlayerStateChangeEvent() {
      // 抛出播放状态变化的事件
      this.player.on('playStateChange', (status) => {
        this.$emit('playStateChange', { status });
        const { loopPlayConfig } = this.config;

        // 如果开启了循环播放，需要在播放结束时处理一下重新播放的逻辑
        if (loopPlayConfig.isLoopPlay) {
          const { loopPlayCount, customLoopPlayCount } = loopPlayConfig;
          const maxLoopPlayCount = loopPlayCount === 'custom' ? +customLoopPlayCount : +loopPlayCount;
          let state = status;
          if (typeof status === 'object') {
            state = status.state;
          }
          if (state === playState.end && this.currentLoopPlayCount < maxLoopPlayCount) {
            this.currentLoopPlayCount += 1;
            logger('loop play');
            this.player.play();
          }
        }
      });
    },

    initPlayerFullscreenEvent() {
      this.player.on('browserFullscreenChange', (isFullScreen) => {
        this.$bus.$emit('fullScreenChange', this, {
          isFullScreen,
        });
      });

      if (this.config.videoType === videoTypes.popup || this.inMagic) {
        if (this.player.getPoster) {
          this.defaultPoster = this.player.getPoster();
        } else {
          this.player.on('ready', () => {
            this.defaultPoster = this.player.getPoster();
          });
        }
        this.player.on('windowFullscreenChange', (isFull) => {
          if (!isFull) {
            this.player.pause();
          }
        });
      }

      if (this.config.showFullScreen || this.inMagic) {
        this.player.on('adStart', () => {
          this.player.enterWindowFullscreen();
        });
        this.player.on('playStateChange', (state) => {
          if (state === playState.playing) {
            this.player.enterWindowFullscreen();
          }
        });
      }
    },

    initAutoplayPolyfill() {
      const { videoConfig } = this.config;
      if (videoConfig.autoplay || this.config.videoType !== videoTypes.transparentHotInline) {
        this.isPlayed = true;

        if (videoConfig.autoplay) {
          this.autoPlayPolyfill();
        }
      }
    },

    playHandler() {
      this.player.play();
      this.isPlayed = true;
    },

    outerPlayHandler() {
      if (!this.videoShow) {
        this.playHandler();
      }
    },

    // 兼容特殊场景下的自动播放
    autoPlayPolyfill() {
      if (this.env.isWechat) {
        setTimeout(() => {
          const video = document.querySelector(`#${playerContainerPrefix}${this.config.id} video`);
          if (window.WeixinJSBridge) {
            window.WeixinJSBridge.invoke(
              'getNetworkType',
              {},
              () => {
                video.play();
              },
              false,
            );
          } else {
            document.addEventListener(
              'WeixinJSBridgeReady',
              () => {
                window.WeixinJSBridge.invoke('getNetworkType', {}, () => {
                  video.play();
                });
              },
              false,
            );
          }
        }, 2000);
      }
    },
  },
};
</script>

<style lang="scss">
  .magic-ui-video {
    > * {
      visibility: hidden;
    }

    .img_wrapper {
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 1);

      img {
        width: 100%;
        height: 100%;
      }

      .icon_play {
        width: 50px;
        height: 50px;
        position: absolute;
        z-index: 999;
        left: 50%;
        top: 50%;
        margin-top: -25px;
        margin-left: -25px;
      }

      &__transparent {
        width: 100%;
        height: 100%;
        background-color: rgba(135, 206, 250, 0.3);
      }

      &__none {
        width: 100%;
        height: 100%;
      }
    }

    &.magic-ui-video--play {
      > * {
        visibility: visible;
      }
    }

    .video_wrapper {
      width: 100%;
      height: 100%;
    }

    // 隐藏ui控制栏
    &.hide_ui_control {
      // 重新播放按钮
      .txp_btn_replay {
        display: none;
      }
    }

    // 播放器遮罩层 点击会暂停/播放视频
    &.disable_interact {
      .txp_shadow {
        display: none;
      }
    }

    &.video_object_fit_fill {
      video {
        object-fit: fill;
      }
    }

    &.video_object_fit_cover {
      video {
        object-fit: cover;
      }
    }
  }
</style>
